import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../pages/dashboard/DashboardUI.module.css';
import settingsStyles from '../../pages/dashboard/Settings.module.css';
import ConfirmDialog from '../ConfirmDialog';
import ReorderListModal from '../ReorderListModal';

const COLOR_PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#84cc16',
  '#6366f1', '#14b8a6', '#ef4444', '#6b7280'
];

const COLOR_NAMES = [
  'Blue', 'Emerald', 'Amber', 'Violet',
  'Pink', 'Cyan', 'Orange', 'Lime',
  'Indigo', 'Teal', 'Red', 'Gray'
];

function getTextColor(bgColor) {
  if (!bgColor) return '#ffffff';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}

const emptyFormData = (extraFields) => ({
  name: '',
  color: '#3b82f6',
  description: '',
  ...Object.fromEntries(extraFields.map((f) => [f.key, false])),
});

/**
 * Shared CRUD UI for any per-company "ordered, colored, named list" entity —
 * lead actions, project statuses, lead statuses. One list screen (name,
 * color, optional description, optional extra checkboxes), one create/edit
 * modal, a reorder modal, and a delete confirmation — all driven by the
 * entity-specific config and service calls the caller supplies.
 */
export default function EntityListManager({
  onMessage,
  title,
  description,
  entityNoun,
  entityNounPlural,
  idKey,
  namePlaceholder,
  load,
  create,
  update,
  remove,
  reorder,
  protectDefaultDelete = false,
  protectDefaultRename = false,
  deleteWarning = (item) => `Are you sure you want to delete "${item.name}"?`,
  extraFields = [],
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [formData, setFormData] = useState(() => emptyFormData(extraFields));

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await load();
      setItems(data || []);
    } catch (error) {
      onMessage({ message: error.message || `Failed to load ${entityNounPlural.toLowerCase()}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [load, onMessage, entityNounPlural]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData(emptyFormData(extraFields));
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      color: item.color || '#6b7280',
      description: item.description || '',
      ...Object.fromEntries(extraFields.map((f) => [f.key, Boolean(item[f.key])])),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData(emptyFormData(extraFields));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onMessage({ message: `${entityNoun} name is required`, type: 'error' });
      return;
    }

    const payload = {
      name: formData.name.trim(),
      color: formData.color,
      description: formData.description.trim(),
      ...Object.fromEntries(extraFields.map((f) => [f.key, formData[f.key]])),
    };

    try {
      if (editingItem) {
        await update(editingItem[idKey], payload);
        onMessage({ message: `${entityNoun} updated successfully`, type: 'success' });
      } else {
        await create(payload);
        onMessage({ message: `${entityNoun} created successfully`, type: 'success' });
      }
      closeModal();
      await loadItems();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || `Failed to save ${entityNoun.toLowerCase()}`;
      onMessage({ message: msg, type: 'error' });
    }
  };

  const handleDelete = async (item) => {
    if (deleting === item[idKey]) return;
    setDeleting(item[idKey]);
    try {
      await remove(item[idKey]);
      onMessage({ message: `${entityNoun} "${item.name}" deleted`, type: 'success' });
      await loadItems();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || `Failed to delete ${entityNoun.toLowerCase()}`;
      onMessage({ message: msg, type: 'error' });
    } finally {
      setDeleting(null);
      setConfirmDelete(null);
    }
  };

  const handleSaveReorder = async (orderedIds) => {
    try {
      setSavingOrder(true);
      const reordered = await reorder(orderedIds);
      setItems(reordered || []);
      setShowReorderModal(false);
      onMessage({ message: `${entityNounPlural} order updated`, type: 'success' });
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || `Failed to reorder ${entityNounPlural.toLowerCase()}`;
      onMessage({ message: msg, type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  if (loading) {
    return <div className={settingsStyles.loading}>Loading {entityNounPlural.toLowerCase()}...</div>;
  }

  return (
    <div>
      <div className={settingsStyles.sectionHeader}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            {title}
          </h3>
          {description && (
            <p className={settingsStyles.cardDesc} style={{ margin: '4px 0 0 0' }}>
              {description}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {reorder && items.length > 1 && (
            <button className={styles.buttonSecondary} onClick={() => setShowReorderModal(true)}>
              Reorder
            </button>
          )}
          <button className={styles.buttonPrimary} onClick={openCreateModal}>
            + New {entityNoun}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className={settingsStyles.emptyState}>
          <p>No {entityNounPlural.toLowerCase()} found.</p>
        </div>
      ) : (
        <div className={settingsStyles.labelsList}>
          {items.map((item) => (
            <div key={item[idKey]} className={settingsStyles.labelCard}>
              <div className={settingsStyles.labelInfo}>
                <span
                  className={settingsStyles.labelBadge}
                  style={{ backgroundColor: item.color || '#6b7280', color: getTextColor(item.color) }}
                >
                  {item.name}
                </span>
                {item.is_default && (
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
                    Default
                  </span>
                )}
                {extraFields.filter((f) => item[f.key]).map((f) => (
                  <span key={f.key} className={settingsStyles.labelDescription}>{f.label}</span>
                ))}
                {item.description && (
                  <span className={settingsStyles.labelDescription}>{item.description}</span>
                )}
              </div>
              <div className={settingsStyles.roleActions}>
                <button className={styles.buttonSecondary} onClick={() => openEditModal(item)}>
                  Edit
                </button>
                {(!protectDefaultDelete || !item.is_default) && (
                  <button
                    className={styles.buttonDanger}
                    onClick={() => setConfirmDelete(item)}
                    disabled={deleting === item[idKey]}
                  >
                    {deleting === item[idKey] ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className={settingsStyles.modalOverlay} onClick={closeModal}>
          <div className={settingsStyles.modal} onClick={(e) => e.stopPropagation()} style={{ minHeight: 'auto' }}>
            <div className={settingsStyles.modalHeader}>
              <h3>{editingItem ? `Edit ${entityNoun}` : `Create ${entityNoun}`}</h3>
              <button className={settingsStyles.closeButton} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className={settingsStyles.modalContent}>
              <form onSubmit={handleSubmit}>
                <div className={settingsStyles.formGroup}>
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={namePlaceholder}
                    autoFocus
                    disabled={protectDefaultRename && editingItem?.is_default}
                  />
                  {protectDefaultRename && editingItem?.is_default && (
                    <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: '4px 0 0' }}>
                      Default {entityNounPlural.toLowerCase()} cannot be renamed.
                    </p>
                  )}
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Color</label>
                  <div className={settingsStyles.colorPicker}>
                    {COLOR_PALETTE.map((color, index) => (
                      <button
                        key={color}
                        type="button"
                        className={`${settingsStyles.colorSwatch} ${formData.color === color ? settingsStyles.colorSwatchActive : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({ ...formData, color })}
                        title={COLOR_NAMES[index]}
                        aria-label={COLOR_NAMES[index]}
                      />
                    ))}
                  </div>
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Preview</label>
                  <div>
                    <span
                      className={settingsStyles.labelBadge}
                      style={{ backgroundColor: formData.color, color: getTextColor(formData.color) }}
                    >
                      {formData.name || `${entityNoun} Preview`}
                    </span>
                  </div>
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={`What does this ${entityNoun.toLowerCase()} mean?`}
                    rows={2}
                  />
                </div>

                {extraFields.map((f) => (
                  <div key={f.key} className={settingsStyles.formGroup}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(formData[f.key])}
                        onChange={(e) => setFormData({ ...formData, [f.key]: e.target.checked })}
                        style={{ width: 'auto', margin: 0 }}
                      />
                      {f.label}
                    </label>
                    {f.description && (
                      <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: '4px 0 0' }}>{f.description}</p>
                    )}
                  </div>
                ))}

                <div className={settingsStyles.modalActions}>
                  <button className={styles.buttonSecondary} onClick={closeModal} type="button">
                    Cancel
                  </button>
                  <button className={styles.buttonPrimary} type="submit">
                    {editingItem ? 'Save Changes' : `Create ${entityNoun}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          message={deleteWarning(confirmDelete)}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {reorder && (
      <ReorderListModal
        isOpen={showReorderModal}
        title={`Reorder ${entityNounPlural}`}
        description={`Drag ${entityNounPlural.toLowerCase()} into the order they should appear across lists and filters.`}
        items={items}
        getId={(item) => item[idKey]}
        getLabel={(item) => item.name}
        getColor={(item) => item.color}
        onClose={() => setShowReorderModal(false)}
        onSave={handleSaveReorder}
        saving={savingOrder}
      />
      )}
    </div>
  );
}
